import React, { useState, useRef } from 'react';
import { Music, Upload, X, Download, Loader2, FileAudio } from 'lucide-react';
import logo from '../assets/logo.png';
import './AudioConverterPage.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

export const AudioConverterPage = ({ session }) => {
    const [files, setFiles] = useState([]);
    const [bitrate, setBitrate] = useState('192k');
    const [converting, setConverting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const fileInputRef = useRef(null);

    const supportedFormats = ['mp3', 'wav', 'flac', 'ogg', 'm4a', 'aac', 'wma', 'aiff', 'opus', 'webm'];

    const handleFileSelect = (e) => {
        const selectedFiles = Array.from(e.target.files);
        setError('');
        setSuccess('');

        // Validate files
        const validFiles = selectedFiles.filter(file => {
            const ext = file.name.split('.').pop().toLowerCase();
            return supportedFormats.includes(ext);
        });

        if (validFiles.length !== selectedFiles.length) {
            setError(`Some files were skipped. Supported formats: ${supportedFormats.join(', ')}`);
        }

        setFiles(prev => [...prev, ...validFiles]);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const droppedFiles = Array.from(e.dataTransfer.files);

        const validFiles = droppedFiles.filter(file => {
            const ext = file.name.split('.').pop().toLowerCase();
            return supportedFormats.includes(ext);
        });

        if (validFiles.length !== droppedFiles.length) {
            setError(`Some files were skipped. Supported formats: ${supportedFormats.join(', ')}`);
        }

        setFiles(prev => [...prev, ...validFiles]);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const removeFile = (index) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const clearAll = () => {
        setFiles([]);
        setError('');
        setSuccess('');
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleConvert = async () => {
        if (!files.length) {
            setError('Please select at least one audio file');
            return;
        }

        if (!session?.access_token) {
            setError('Please log in to convert files');
            return;
        }

        setConverting(true);
        setError('');
        setSuccess('');

        const formData = new FormData();
        files.forEach(file => {
            formData.append('files', file);
        });
        formData.append('bitrate', bitrate);

        try {
            const response = await fetch(`${API_BASE}/api/audio/convert`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${session.access_token}`,
                },
                body: formData,
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Conversion failed');
            }

            // Download from signed URL - fetch as blob to force download
            if (data.download_url) {
                const fileResponse = await fetch(data.download_url);
                const blob = await fileResponse.blob();
                const blobUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = data.filename || 'converted_audio.mp3';
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(blobUrl);
            }

            const countMsg = data.file_count > 1
                ? `${data.file_count} files`
                : '1 file';
            setSuccess(`Successfully converted ${countMsg}!`);

            if (data.warnings?.length) {
                setError(`Note: ${data.warnings.join(', ')}`);
            }

            setFiles([]);
        } catch (err) {
            setError(err.message);
        } finally {
            setConverting(false);
        }
    };

    const bitrateOptions = [
        { value: '128k', label: '128 kbps', desc: 'Good quality, smaller files' },
        { value: '192k', label: '192 kbps', desc: 'Great quality (recommended)' },
        { value: '256k', label: '256 kbps', desc: 'High quality' },
        { value: '320k', label: '320 kbps', desc: 'Maximum quality' },
    ];

    return (
        <div className="page-container audio-converter-page">
            <div className="converter-header">
                <h1>Audio Converter</h1>
                <p>Convert any audio file to MP3</p>
            </div>

            {/* Upload Area */}
            <div
                className={`upload-zone ${files.length ? 'has-files' : ''}`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => fileInputRef.current?.click()}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={supportedFormats.map(f => `.${f}`).join(',')}
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                />

                <div className="upload-zone-content">
                    <Upload size={48} className="upload-icon" />
                    <h3>Drop audio files here</h3>
                    <p>or click to browse</p>
                    <span className="supported-formats">
                        Supports: {supportedFormats.join(', ').toUpperCase()}
                    </span>
                </div>
            </div>

            {/* File List */}
            {files.length > 0 && (
                <div className="file-list-section">
                    <div className="file-list-header">
                        <h3><FileAudio size={20} /> {files.length} file{files.length > 1 ? 's' : ''} selected</h3>
                        <button className="btn-text" onClick={clearAll}>Clear all</button>
                    </div>

                    <ul className="file-list">
                        {files.map((file, index) => (
                            <li key={index} className="file-item">
                                <div className="file-info">
                                    <Music size={18} />
                                    <span className="file-name">{file.name}</span>
                                    <span className="file-size">{formatFileSize(file.size)}</span>
                                </div>
                                <button
                                    className="remove-file-btn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        removeFile(index);
                                    }}
                                >
                                    <X size={16} />
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Settings */}
            <div className="converter-settings">
                <div className="setting-group">
                    <label>Output Quality</label>
                    <div className="bitrate-options">
                        {bitrateOptions.map(option => (
                            <button
                                key={option.value}
                                className={`bitrate-option ${bitrate === option.value ? 'active' : ''}`}
                                onClick={() => setBitrate(option.value)}
                                disabled={converting}
                            >
                                <strong>{option.label}</strong>
                                <span>{option.desc}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Error/Success Messages */}
            {error && <div className="message error-message">{error}</div>}
            {success && <div className="message success-message">{success}</div>}

            {/* Convert Button */}
            <button
                className="btn-convert"
                onClick={handleConvert}
                disabled={!files.length || converting}
            >
                {converting ? (
                    <>
                        <Loader2 size={20} className="spin" />
                        Converting...
                    </>
                ) : (
                    <>
                        <Download size={20} />
                        Convert to MP3 {files.length > 1 && `(${files.length} files)`}
                    </>
                )}
            </button>

            {/* Info Section */}
            <section className="converter-info">
                <h2>Fast & Free Audio Conversion</h2>
                <div className="info-grid">
                    <div className="info-card">
                        <span className="info-icon">⚡</span>
                        <h3>Lightning Fast</h3>
                        <p>Convert multiple files in seconds with server-side processing</p>
                    </div>
                    <div className="info-card">
                        <span className="info-icon">🔒</span>
                        <h3>Private & Secure</h3>
                        <p>Files are automatically deleted after conversion</p>
                    </div>
                    <div className="info-card">
                        <span className="info-icon">📦</span>
                        <h3>Batch Support</h3>
                        <p>Upload multiple files at once, download as ZIP</p>
                    </div>
                    <div className="info-card">
                        <span className="info-icon">🎵</span>
                        <h3>High Quality</h3>
                        <p>Choose your bitrate from 128k to 320k</p>
                    </div>
                </div>
            </section>
        </div>
    );
};

